import { AxiosError, AxiosResponse } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext, RolodexEntryQueryBody } from 'features/rolodex'
import React, { useMemo } from 'react'
import useSWR from 'swr'
import { FetcherType } from 'types'

export type RolodexIdQueryResult = {
  ids: readonly string[] | undefined
  idsByQuery: ReadonlyArray<readonly string[]> | undefined
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
  /** To account for the initial case before the query fires  */
  hasEverLoaded: boolean
  /** Any errors returned from SWR */
  error: AxiosError
  /** A callback for forcing a re-fetch */
  forceRefresh: () => Promise<void>
}

export function useRolodexIds(
  queryOrQueries:
    | RolodexEntryQueryBody
    | ReadonlyArray<RolodexEntryQueryBody>
    | undefined
): RolodexIdQueryResult {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  const queryBodiesStr = React.useMemo(() => {
    if (!queryOrQueries || !isAuthenticated) return '[]'
    const arr = Array.isArray(queryOrQueries)
      ? queryOrQueries
      : [queryOrQueries]
    const queryBodiesJSON = arr.map((body) => {
      const bodyCopy = { ...body }
      if (!bodyCopy.limit) bodyCopy.limit = body.ids?.length ?? 10000
      bodyCopy.tags = { ...bodyCopy.tags }
      return JSON.stringify(bodyCopy) // Ensures unique strings that work well with SWR
    })
    return JSON.stringify(queryBodiesJSON) // Allows this to be used in subsequent dep-arrays, even if queryOrQueries is not memo'd
  }, [queryOrQueries, isAuthenticated])

  // Doing this in 2 steps ensures that the caller doesn't have to memoize a `queryOrQueries` object
  const queryBodies: string[] = React.useMemo(
    () => JSON.parse(queryBodiesStr),
    [queryBodiesStr]
  )

  // ----------------------------------------------------------------------------
  // Rolodex "Proper" Fetch

  const fetcher = React.useCallback(
    (keys: string[]) =>
      Promise.all(
        keys.map(
          async (body) =>
            await axios
              // THIS IS THE BIT THAT MAKES THIS JUST 'ids' =
              .post(`${apiURL}/api/v2/entries/load/ids`, body, {
                headers: {
                  'Content-Type': 'application/json',
                },
              })
              .then(async (response: AxiosResponse) => {
                const result = response.data
                if (!Array.isArray(result)) {
                  console.error(
                    'Rolodex entry fetch failed',
                    JSON.parse(body),
                    result
                  )
                  return []
                }
                return result
              })
        )
      ),
    [apiURL, axios]
  ) as FetcherType<string[][]>

  const {
    data: rolodexProperFetchData,
    isLoading,
    isValidating,
    error: swrError,
    mutate,
  } = useSWR<string[][]>(queryBodies, fetcher)

  return useMemo(
    () => ({
      ids: rolodexProperFetchData?.flat(),
      idsByQuery: rolodexProperFetchData?.map((ids) => ids ?? []),
      isLoading,
      isValidating,
      error: swrError,
      forceRefresh: async () => {
        await mutate()
      },
      hasEverLoaded: !!rolodexProperFetchData,
    }),
    [isLoading, isValidating, mutate, rolodexProperFetchData, swrError]
  )
}
